/**
 * Shared `a` renderer for all of the assistant's Markdown answers.
 *
 * It is invoked in exactly one place: `ChatMessage.tsx`. Since 22.09.2026 the
 * question panel (`OverlayBotChat.tsx`) also renders its answers through it
 * instead of keeping a second Markdown renderer — so the anchor is the same for
 * both, without the panel wiring it in itself.
 *
 * Without it, a link like `[API-Schlüssel](/settings?panel=agent&tab=apiKeys)`
 * is a real browser navigation: the bundle reloads, `Protected` checks the
 * login again, the conversation is gone. In the question panel the
 * conversation lives only in memory — there the reload loss is total.
 *
 * External targets stay `target="_blank"` as before; `mailto:`, `tel:` and
 * `#anchor` stay untouched. Internal links keep their `href` attribute so that
 * right-click → "Open in new tab" and ⌘/Ctrl-click keep working.
 *
 * That is exactly why the `href` here is a security decision, not cosmetics:
 * the answer text comes from model and tool output, and `ChatMessage.tsx`
 * renders it with `rehypeRaw`, raw HTML anchors included. A URL like
 * `/\evil.example/settings` looks portal-internal but is resolved off-origin by
 * the browser. The "internal?" decision is therefore made **not** here but in
 * `isInternalGuideHref` — the same check the navigator uses on click. Whatever
 * it rejects gets no `href` at all: then the context menu, ⌘-click or middle
 * mouse button offer nothing either.
 */

import React from 'react';

import { isInternalGuideHref, useGuideNavigate } from '../guide/useGuideNavigate';

/** `http(s)://…` and protocol-relative `//host/…` leave the portal. */
const EXTERNAL_HREF = /^(https?:)?\/\//i;

/**
 * Schemes that may keep an `href` in the fall-through branch. Without this
 * allowlist, defending against `javascript:`/`data:` would rest solely on
 * `defaultUrlTransform` in react-markdown — a default in `node_modules` that a
 * caller switches off with `urlTransform={null}`.
 */
const SAFE_OTHER_HREF = /^(mailto:|tel:|#)/i;

export interface MarkdownLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
    href?: string;
    children?: React.ReactNode;
    /** react-markdown passes the mdast node through; it does not belong in the DOM. */
    node?: unknown;
}

export function MarkdownLink({ href, children, node, onClick, ...rest }: MarkdownLinkProps) {
    const guideNavigate = useGuideNavigate();

    const handleClick = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            onClick?.(event);
            if (event.defaultPrevented) return;

            // ⌘/Ctrl/Shift/Alt-click belongs to the browser: "open in new tab"
            // is preserved that way. The `button` check is belt and braces
            // for synthetic events — in the browser the middle mouse button
            // produces `auxclick`, not `click`, and never reaches `onClick`.
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            if (typeof event.button === 'number' && event.button !== 0) return;

            // Intercept even on 'rejected': a full page load to an unusable
            // URL would be worse than doing nothing at all.
            event.preventDefault();
            guideNavigate(href as string);
        },
        [guideNavigate, href, onClick],
    );

    if (typeof href === 'string' && EXTERNAL_HREF.test(href)) {
        return (
            <a {...rest} href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}>
                {children}
            </a>
        );
    }

    if (isInternalGuideHref(href)) {
        return (
            <a {...rest} href={href} onClick={handleClick}>
                {children}
            </a>
        );
    }

    // mailto:, tel:, #anchor → default anchor behavior. Everything else
    // (backslash URLs, unfilled placeholders, relative paths, unknown schemes)
    // loses its `href` instead of offering a broken or foreign navigation.
    const safeHref = typeof href === 'string' && SAFE_OTHER_HREF.test(href.trim()) ? href : undefined;
    return (
        <a {...rest} href={safeHref} onClick={onClick}>
            {children}
        </a>
    );
}

/**
 * Ready-made `components` block for `<Markdown components={markdownLinkComponents}>`.
 *
 * `ChatMessage` layers its `div` renderer (embedded resources) on top; anyone
 * who only needs links passes it through unchanged. That leaves exactly one
 * path to this anchor instead of two spellings side by side.
 */
export const markdownLinkComponents = { a: MarkdownLink };
